/**
 * DailyFinancialMetrics Component
 *
 * This component displays the financial metrics for either:
 * a) A full day's shifts with aggregated metrics from DailyTicket
 * b) A single shift's metrics directly from the shift document
 **/

import PropTypes from 'prop-types';
import { Typography } from '@mui/material';
import { MoneyIcon, LaborCostIcon, TablesIcon, CoversIcon } from '../../assets/svg';
import { currencyFormatter } from '../../utils/helpers/formatting/currencyHelpers';
import { formatDuration } from '../../utils/helpers/shifts/shiftsHelpers';
import { calculateTimeDifference } from '../../utils/helpers/timeUtils';
import {
  DailyFinancialMetricsContainer,
  CategoryContainer,
  CategoryItemsContainer,
} from './DailyFinancialMetrics.styles';

export const DailyFinancialMetrics = ({ shifts, liveTicket, isShiftLevel = false }) => {
  // Calculate time-based metrics
  const timeScheduledInMinutes = shifts.reduce((_, shift) => _ + calculateTimeDifference(shift.start, shift.end), 0);
  const timeWorkedInMinutes = shifts.reduce(
    (_, shift) => _ + calculateTimeDifference(shift.clockedInAt, shift.clockedOutAt),
    0
  );

  // Labels for context
  const numOfShiftsLabel = !isShiftLevel && shifts.length > 1 ? ` (${shifts.length} shifts)` : '';
  const costLabel = ` (${formatDuration(timeWorkedInMinutes) || '0h'})`;
  const projectedCostLabel = ` (${formatDuration(timeScheduledInMinutes) || 0})`;

  const shift = shifts?.[0] || {};

  const net = isShiftLevel ? shift.revenue || 0 : liveTicket?.net || 0;
  const projectedNet = isShiftLevel ? 0 : liveTicket?.projectedNet || 0;
  const cost = isShiftLevel ? shift.cost || 0 : liveTicket?.cost || 0;
  const projectedCost = isShiftLevel ? shift.projectedCost || 0 : liveTicket?.projectedCost || 0;
  const costPercentage = isShiftLevel
    ? shift.revenue
      ? parseFloat(((shift.cost || 0) / shift.revenue) * 100).toFixed(2)
      : 0
    : liveTicket?.costPercentage || 0;
  const projectedCostPercentage = isShiftLevel ? 0 : liveTicket?.projectedCostPercentage || 0;
  const tables = isShiftLevel ? shift.tables || 0 : liveTicket?.tables || 0;
  const covers = isShiftLevel ? shift.covers || 0 : liveTicket?.covers || 0;

  return (
    <DailyFinancialMetricsContainer>
      <Typography variant="calloutRegular">
        {`${costPercentage || 0}% cost of labor${isShiftLevel ? '' : `, ${projectedCostPercentage || 0}% projected`}${numOfShiftsLabel}`}
      </Typography>

      <CategoryContainer className="net-sales">
        <MoneyIcon />
        <CategoryItemsContainer>
          <Typography variant="calloutRegular">{`${currencyFormatter(net || 0)} net sales`}</Typography>
          {!isShiftLevel && (
            <Typography variant="calloutRegular">{`${currencyFormatter(projectedNet || 0)} projected`}</Typography>
          )}
        </CategoryItemsContainer>
      </CategoryContainer>

      <CategoryContainer className="labor-cost">
        <LaborCostIcon />
        <CategoryItemsContainer>
          <Typography variant="calloutRegular">{`${currencyFormatter(cost || 0)} cost of labor${costLabel}`}</Typography>
          <Typography variant="calloutRegular">{`${currencyFormatter(projectedCost || 0)} projected${projectedCostLabel}`}</Typography>
        </CategoryItemsContainer>
      </CategoryContainer>

      {/* Tables sat and covers are hidden when displaying shift-level information because these metrics are not yet stored at the shift level in our database,
      so we can avoid showing zero-value metrics */}
      {(tables > 0 || !isShiftLevel) && (
        <CategoryContainer className="service-info">
          <TablesIcon />
          <CategoryItemsContainer>
            <Typography variant="calloutRegular">{`${tables || 0} tables sat`}</Typography>
          </CategoryItemsContainer>
        </CategoryContainer>
      )}

      {(covers > 0 || !isShiftLevel) && (
        <CategoryContainer className="service-info">
          <CoversIcon />
          <CategoryItemsContainer>
            <Typography variant="calloutRegular">{`${covers || 0} covers`}</Typography>
          </CategoryItemsContainer>
        </CategoryContainer>
      )}
    </DailyFinancialMetricsContainer>
  );
};

DailyFinancialMetrics.propTypes = {
  shifts: PropTypes.arrayOf(
    PropTypes.shape({
      start: PropTypes.number,
      end: PropTypes.number,
      clockedInAt: PropTypes.number,
      clockedOutAt: PropTypes.number,
      revenue: PropTypes.number,
      cost: PropTypes.number,
      projectedCost: PropTypes.number,
      tables: PropTypes.number,
      covers: PropTypes.number,
    })
  ).isRequired,
  liveTicket: PropTypes.shape({
    net: PropTypes.number,
    projectedNet: PropTypes.number,
    cost: PropTypes.number,
    projectedCost: PropTypes.number,
    costPercentage: PropTypes.number,
    projectedCostPercentage: PropTypes.number,
    tables: PropTypes.number,
    covers: PropTypes.number,
  }),
  isShiftLevel: PropTypes.bool,
};
